import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider, ConfirmProvider } from './components/ui';
import ErrorBoundary from './components/common/ErrorBoundary';
import ProtectedRoute from './components/auth/ProtectedRoute';
import LandingPage from './pages/Landing';
import LoginPage from './pages/Login';
import RegisterPage from './pages/Register';
import ResetPasswordPage from './pages/ResetPassword';
import './styles/globals.css';

const DashboardLayout = React.lazy(() => import('./pages/Dashboard'));
// Public: the page a teammate is sent before they have set anything up. Lazy, because
// nobody signing in needs it in their bundle.
const IntegrationsPage = React.lazy(() => import('./pages/Integrations'));
// Where "Sign in with BOS" returns. Lazy: only reached when single sign-on is switched on.
const BosSignInCompletePage = React.lazy(() => import('./pages/BosSignInComplete'));

function App() {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    // High contrast follows the operating system. It used to come from a switch on the old
    // Setup page, which is gone; reading that stored value would have left anyone who once
    // turned it on stuck with it, and nowhere in the app to turn it off. Reduced motion
    // already follows the OS in globals.css, and text size is the browser's zoom.
    const contrast = window.matchMedia('(prefers-contrast: more)');

    // localStorage can hold anything (a corrupt or hand-edited value, a quota error, or
    // a browser that blocks site data). An unguarded JSON.parse here threw during boot
    // and left the app unrenderable, so every read is defensive.
    const readJSON = (key: string): any => {
      try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : {};
      } catch {
        return {};
      }
    };

    const readString = (key: string): string | null => {
      try {
        return localStorage.getItem(key);
      } catch {
        return null;
      }
    };

    const applyTheme = () => {
      const authUser = readJSON('auth_user');
      const storageSuffix = String(authUser?.email || 'guest').toLowerCase();
      // The day/night switch in the top bar writes this key; despite its name it is not one
      // of the old Setup page's settings, and it is still live.
      const storedMode = String(readString(`pm_setup_theme_mode_${storageSuffix}`) || readString('pm_setup_theme_mode') || 'day').replace(/"/g, '');

      const resolvedTheme = storedMode === 'night'
        ? 'dark'
        : storedMode === 'auto'
          ? (media.matches ? 'dark' : 'light')
          : 'light';

      document.documentElement.setAttribute('data-theme', resolvedTheme);
      document.documentElement.removeAttribute('data-panel-theme');
      document.documentElement.setAttribute('data-contrast', contrast.matches ? 'high' : 'normal');
      document.documentElement.style.colorScheme = resolvedTheme;
      // The brand tokens are NOT set inline here. They are defined per theme in
      // globals.css, and hardcoding them on the root element took precedence over the
      // stylesheet — which pinned the palette to values that fail WCAG AA contrast
      // (#1db8a4 measures 2.49:1 on white) and defeated the dark-theme overrides.
    };

    applyTheme();
    const handleThemeChange = () => applyTheme();

    media.addEventListener?.('change', handleThemeChange);
    contrast.addEventListener?.('change', handleThemeChange);
    window.addEventListener('storage', handleThemeChange);
    window.addEventListener('pm-local-storage-change', handleThemeChange as EventListener);

    return () => {
      media.removeEventListener?.('change', handleThemeChange);
      contrast.removeEventListener?.('change', handleThemeChange);
      window.removeEventListener('storage', handleThemeChange);
      window.removeEventListener('pm-local-storage-change', handleThemeChange as EventListener);
    };
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
         <ToastProvider>
          <ConfirmProvider>
          {/* Uses theme tokens rather than a hardcoded dark background, which used to
              flash dark before the light theme painted. */}
          <React.Suspense fallback={
            <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-base)', color: 'var(--brand-primary)' }}>
              Loading…
            </div>
          }>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/integrations" element={<IntegrationsPage />} />
              <Route path="/auth/bos/complete" element={<BosSignInCompletePage />} />
              <Route
                path="/portal/*"
                element={
                  <ProtectedRoute>
                    <DashboardLayout />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </React.Suspense>
          </ConfirmProvider>
         </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
